  <!--
  PuppyFormItem 组件。
  定位：
  - 作为属性栏中的通用“左图标 + 右表单”布局容器。
  - 统一承载 SolidWorks 风格的图标按钮、选择态和禁用态。
  功能：
  - 左侧图标区支持可选中交互。
  - 右侧表单区仅负责插槽承载，不介入具体表单实现。
  - 可通过 selected / disabled / canSelect 控制外观和行为。
-->
<template>
  <div
    class="puppy-form-item"
    :class="{
      'is-disabled': disabled,
    }"
  >
    <div class="puppy-form-item__icon w-25% min-w-28px min-h-28px flex items-center justify-center">
      <button
          :class="{'can-select': canSelect,'is-clickable': canSelect && !disabled,
        'is-selected': selected,}"
          :disabled="disabled || !canSelect"
          :tabindex="canSelect && !disabled ? 0 : -1"
          @click="handleIconClick"
          @keydown.enter.prevent="handleIconKeydown"
          @keydown.space.prevent="handleIconKeydown"
      >
        <slot name="icon" />
      </button>
    </div>

    <div class="puppy-form-item__body">
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * PuppyFormItem 组件的实现层。
 *
 * 设计目标：
 * - 把属性栏中大量重复出现的“图标 + 表单”布局抽象为统一组件。
 * - 让图标区既可作为普通装饰，也可作为可点击的状态入口。
 */
const props = withDefaults(defineProps<{
  /**
   * 图标区是否允许点击选择。
   */
  canSelect?: boolean;
  /**
   * 当前是否处于选中状态。
   */
  selected?: boolean;
  /**
   * 当前是否禁用。
   */
  disabled?: boolean;
}>(), {
  canSelect: false,
  selected: false,
  disabled: false,
});

const emit = defineEmits<{
  /**
   * 点击图标区时触发。
   */
  (event: 'icon-click', payload: Event): void;
  /**
   * 图标区触发选中行为时触发。
   */
  (event: 'select', payload: Event): void;
}>();

/**
 * 处理图标区点击。
 *
 * @param event 鼠标事件。
 */
function handleIconClick(event: MouseEvent): void {
  if (props.disabled || !props.canSelect) {
    return;
  }
  emit('icon-click', event);
  emit('select', event);
}

function handleIconKeydown(event: KeyboardEvent): void {
  if (event.key !== 'Enter' && event.key !== ' ') {
    return;
  }
  event.preventDefault();
  handleIconClick(event as unknown as MouseEvent);
}
</script>

<style scoped lang="less">
.puppy-form-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
}

.puppy-form-item__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  color: inherit;

  button {
    border: 0 solid transparent;
    background: transparent;
    transition: border-color 0.15s ease, background-color 0.15s ease, opacity 0.15s ease;
  }
}

.can-select {
  border-radius: 4px;
  border:1px solid var(--n-title-text-color-disabled) !important;
  width: 28px;
  height: 28px;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.is-clickable {
  cursor: pointer;
}

.is-selected {
  background: rgba(127, 127, 127, 0.18) !important;
}

.is-disabled {
  opacity: 0.6;
}

.puppy-form-item__body {
  flex: 1 1 auto;
  min-width: 0;
}
</style>
